import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, Loader2, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { OTPInput } from '@/components/ui/otp-input';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';

const LOGO_URL = '/netlen-logo.svg';

export const Login = ({ onLogin }) => {
  const { theme } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  // 2FA State
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFAMethod, setTwoFAMethod] = useState(null);
  const [sessionToken, setSessionToken] = useState('');
  const [verificationCode, setVerificationCode] = useState('');

  // Check for token login from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const loginToken = urlParams.get('token');
    
    if (loginToken) {
      handleTokenLogin(loginToken);
    }
  }, []);

  const handleTokenLogin = async (token) => {
    setIsLoading(true);
    setError('');
    
    try {
      const response = await api.tokenLogin(token);
      
      if (response.requires_2fa) {
        setRequires2FA(true);
        setTwoFAMethod(response.two_fa_method);
        setSessionToken(response.session_token);
        setEmail(response.user?.email || '');
      } else if (response.access_token) {
        onLogin(response.user);
      }
    } catch (err) {
      setError(err.message || 'Token ile giriş başarısız');
    } finally {
      setIsLoading(false);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    try {
      const response = await api.login(email, password);
      
      if (response.requires_2fa) {
        setRequires2FA(true);
        setTwoFAMethod(response.two_fa_method);
        setSessionToken(response.session_token);
      } else if (response.access_token) {
        onLogin(response.user);
      }
    } catch (err) {
      setError(err.message || 'Giriş başarısız');
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    if (e) e.preventDefault();
    setError('');
    setIsLoading(true);
    
    try {
      const response = await api.verify2FA(email, verificationCode, sessionToken);
      
      if (response.access_token) {
        onLogin(response.user);
      }
    } catch (err) {
      setError(err.message || 'Doğrulama kodu geçersiz');
      setVerificationCode(''); // Clear code on error
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-submit when OTP is complete
  const handleOTPComplete = async (code) => {
    if (code.length !== 6) return;
    setVerificationCode(code);
    setError('');
    setIsLoading(true);
    
    try {
      const response = await api.verify2FA(email, code, sessionToken);
      
      if (response.access_token) {
        onLogin(response.user);
      }
    } catch (err) {
      setError(err.message || 'Doğrulama kodu geçersiz');
      setVerificationCode('');
    } finally {
      setIsLoading(false);
    }
  };

  // 2FA Verification Form
  if (requires2FA) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-gradient-to-bl from-blue-500/5 to-transparent rounded-full blur-3xl" />
          <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-gradient-to-tr from-indigo-500/5 to-transparent rounded-full blur-3xl" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="glass rounded-2xl border border-white/20 dark:border-slate-700/50 shadow-2xl shadow-slate-200/50 dark:shadow-slate-950/50 p-8 md:p-10">
            <div className="flex justify-center mb-6">
              <div className="p-4 rounded-full bg-blue-100 dark:bg-blue-900/30">
                <Shield className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
            </div>

            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-foreground mb-2">İki Faktörlü Doğrulama</h1>
              <p className="text-muted-foreground text-sm">
                {twoFAMethod === 'sms' 
                  ? 'Telefonunuza gönderilen kodu girin'
                  : 'Authenticator uygulamanızdaki kodu girin'}
              </p>
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm text-center"
              >
                {error}
              </motion.div>
            )}

            <form onSubmit={handle2FASubmit} className="space-y-5">
              <div className="space-y-3">
                <Label className="text-sm font-medium text-center block">Doğrulama Kodu</Label>
                <OTPInput
                  length={6}
                  value={verificationCode}
                  onChange={setVerificationCode}
                  onComplete={handleOTPComplete}
                  disabled={isLoading}
                />
              </div>

              <Button
                type="submit"
                disabled={isLoading || verificationCode.length !== 6}
                className="w-full h-12 rounded-full bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:hover:bg-slate-200 dark:text-slate-900 text-white font-semibold shadow-lg hover:shadow-xl transition-all active:scale-[0.98]"
                data-testid="2fa-submit-button"
              >
                {isLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  'Doğrula'
                )}
              </Button>

              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setRequires2FA(false);
                  setVerificationCode('');
                  setSessionToken('');
                }}
                className="w-full"
              >
                Geri Dön
              </Button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-gradient-to-bl from-blue-500/5 to-transparent rounded-full blur-3xl" />
        <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-gradient-to-tr from-indigo-500/5 to-transparent rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        {/* Glass Card */}
        <div className="glass rounded-2xl border border-white/20 dark:border-slate-700/50 shadow-2xl shadow-slate-200/50 dark:shadow-slate-950/50 p-8 md:p-10">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <motion.img
              src={LOGO_URL}
              alt="Netlen"
              className={`h-12 w-auto ${theme === 'dark' ? 'logo-dark-mode' : ''}`}
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            />
          </div>

          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-foreground mb-2">Webmail'e Hoş Geldiniz</h1>
            <p className="text-muted-foreground text-sm">Email hesabınızla giriş yapın</p>
          </div>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm text-center"
            >
              {error}
            </motion.div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">Email Adresi</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="ornek@domain.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-11 rounded-xl bg-background/50 focus:bg-background transition-colors"
                  data-testid="login-email-input"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">Şifre</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 h-11 rounded-xl bg-background/50 focus:bg-background transition-colors"
                  data-testid="login-password-input"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full h-12 rounded-full bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:hover:bg-slate-200 dark:text-slate-900 text-white font-semibold shadow-lg hover:shadow-xl transition-all active:scale-[0.98] compose-btn"
              data-testid="login-submit-button"
            >
              {isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                'Giriş Yap'
              )}
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-border/50 text-center">
            <p className="text-xs text-muted-foreground">
              Güvenli bağlantı ile email hesabınıza erişin
            </p>
          </div>
        </div>

        {/* Brand Footer */}
        <div className="text-center mt-6">
          <p className="text-xs text-muted-foreground">
            Powered by <span className="font-semibold">Netlen</span>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
